import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

// Lightweight guided tour — no dependencies. Spotlights each target element (found by its
// data-tour attribute), floats a tooltip beside it, and advances Back / Next / Done, hovering
// smoothly from one element to the next. Copy only — PII-free. Shared by the Print Ops pages.
//
// steps: [{ target: 'data-tour value', title, body, tag? }]
// open/onClose: controlled by the host page. storageKey: localStorage flag so a first-visit
// auto-start only fires once (set on finish/skip).
export default function GuidedTour({ steps, open, onClose, storageKey }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [tipH, setTipH] = useState(0);   // measured tooltip height, for on-screen placement
  const tipRef = useRef(null);
  const prevFocus = useRef(null);
  const step = steps[i];

  const measure = useCallback(() => {
    const s = steps[i];
    if (!s) return;
    const el = document.querySelector(`[data-tour="${s.target}"]`);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [i, steps]);

  // reset to the first step each time the tour opens
  useEffect(() => { if (open) setI(0); }, [open]);

  // bring the current target into view, then keep the spotlight glued as the page settles.
  // Instant scroll (not smooth) so the rect is final immediately — the ring lands exactly on
  // the target instead of chasing a moving element.
  useEffect(() => {
    if (!open || !step) return undefined;
    const el = document.querySelector(`[data-tour="${step.target}"]`);
    if (el) el.scrollIntoView({ block: 'center', behavior: 'auto' });
    // re-measure across the next several frames so we catch the post-scroll / post-layout box
    let raf; let n = 0;
    const settle = () => { measure(); if (++n < 8) raf = requestAnimationFrame(settle); };
    settle();
    const onMove = () => measure();
    window.addEventListener('resize', onMove);
    window.addEventListener('scroll', onMove, true);
    const id = setInterval(measure, 250); // catch async layout shifts (e.g. data loading in)
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', onMove);
      window.removeEventListener('scroll', onMove, true);
      clearInterval(id);
    };
  }, [open, step, measure]);

  const finish = useCallback(() => {
    if (storageKey) { try { localStorage.setItem(storageKey, '1'); } catch (e) { /* ignore */ } }
    if (onClose) onClose();
  }, [storageKey, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') finish();
      else if (e.key === 'ArrowRight') setI((v) => Math.min(v + 1, steps.length - 1));
      else if (e.key === 'ArrowLeft') setI((v) => Math.max(v - 1, 0));
      else if (e.key === 'Tab') {
        // Keep Tab within the tour so keyboard users can't wander onto the covered page controls.
        const f = tipRef.current ? tipRef.current.querySelectorAll('button') : null;
        if (!f || !f.length) return;
        const first = f[0], lastEl = f[f.length - 1], active = document.activeElement;
        if (e.shiftKey && (active === first || active === tipRef.current)) { e.preventDefault(); lastEl.focus(); }
        else if (!e.shiftKey && active === lastEl) { e.preventDefault(); first.focus(); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, finish, steps.length]);

  // Record the element focused before the tour opened, then restore it on close. This runs in a
  // layout effect declared BEFORE the focus-into-tour effect below, so it captures the real trigger
  // (e.g. the Guide button) rather than the tour tooltip that the next effect is about to focus.
  useLayoutEffect(() => {
    if (!open) return undefined;
    prevFocus.current = document.activeElement;
    return () => {
      const el = prevFocus.current;
      if (typeof el?.focus === 'function') { try { el.focus(); } catch (e) { /* ignore */ } }
    };
  }, [open]);

  // After each step renders, measure the tooltip (for on-screen placement) and pull focus into it.
  useLayoutEffect(() => {
    if (!open || !tipRef.current) return;
    setTipH(tipRef.current.offsetHeight);
    if (!tipRef.current.contains(document.activeElement)) tipRef.current.focus();
  }, [open, i, rect]);

  if (!open || !step) return null;

  const last = i === steps.length - 1;
  const pad = 6;
  const gap = 12;
  const tipW = 320;

  const spotStyle = rect && {
    position: 'fixed', top: rect.top - pad, left: rect.left - pad,
    width: rect.width + pad * 2, height: rect.height + pad * 2,
    borderRadius: 10, border: '2px solid #378ADD',
    boxShadow: '0 0 0 9999px rgba(17,17,16,.55)',
    zIndex: 9998, pointerEvents: 'none', transition: 'all .25s ease',
  };

  // Place the tooltip below the target, flipping above when it fits better there. Positions use the
  // MEASURED tooltip height (tipH, estimated until the first measure) and clamp top so neither the
  // heading nor the Back/Next/Done row can ever land off-screen — the bug a height-blind clamp hit
  // on short viewports or with enlarged text.
  let tipPos;
  if (rect) {
    const m = 12;
    const h = tipH || 180;
    // Clamp with the ACTUAL rendered width (tipW capped by the maxWidth: calc(100vw - 24px)), so a
    // viewport narrower than tipW can't push left negative and clip the tooltip off the left edge.
    const w = Math.min(tipW, window.innerWidth - m * 2);
    const left = Math.max(m, Math.min(Math.max(rect.left, m), window.innerWidth - w - m));
    const belowTop = rect.top + rect.height + pad + gap;
    const aboveTop = rect.top - pad - gap - h;
    const belowFits = belowTop + h <= window.innerHeight - m;
    const aboveFits = aboveTop >= m;
    const top = (belowFits || !aboveFits)
      ? Math.min(belowTop, window.innerHeight - h - m)   // below, never past the bottom edge
      : Math.max(aboveTop, m);                           // above, never past the top edge
    tipPos = { top: Math.max(top, m), left };
  } else {
    tipPos = { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' };
  }

  return createPortal(
    <>
      {/* click-blocking backdrop; the spotlight's own box-shadow does the dimming */}
      <div style={{ position: 'fixed', inset: 0, zIndex: 9997 }} />
      {spotStyle && <div style={spotStyle} />}
      <div
        ref={tipRef}
        role="dialog"
        aria-modal="true"
        aria-label={step.title ? `Guide: ${step.title}` : 'Guided tour'}
        tabIndex={-1}
        style={{
          position: 'fixed', width: tipW, maxWidth: 'calc(100vw - 24px)', ...tipPos,
          // Cap height to the viewport and scroll inside: on a short screen or at high zoom the
          // tooltip can be taller than the space above/below the target, and without this the
          // Back/Next/Done row would fall off-screen out of reach.
          maxHeight: 'calc(100vh - 24px)', overflowY: 'auto',
          background: '#fff', border: '.5px solid rgba(0,0,0,.14)', borderRadius: 12,
          boxShadow: '0 8px 30px rgba(17,17,16,.18)', zIndex: 9999, padding: '14px 16px',
          fontFamily: 'inherit', outline: 'none', transition: 'top .25s ease, left .25s ease',
        }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color: '#9a988f', fontWeight: 500 }}>{(step.tag || 'Guide')} · {i + 1}/{steps.length}</span>
          <button type="button" onClick={finish}
            style={{ border: 'none', background: 'none', color: '#9a988f', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Skip</button>
        </div>
        {step.title && <div style={{ fontSize: 14, fontWeight: 600, color: '#1a1a18', marginBottom: 4 }}>{step.title}</div>}
        <div style={{ fontSize: 13, lineHeight: 1.5, color: '#6b6a64' }}>{step.body}</div>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 12 }}>
          <div style={{ display: 'flex', gap: 5 }}>
            {steps.map((s, k) => (
              <span key={s.target} style={{ width: 6, height: 6, borderRadius: '50%', background: k === i ? '#378ADD' : '#D3D1C7' }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {i > 0 && <button type="button" onClick={() => setI(i - 1)} style={GHOST}>Back</button>}
            {last
              ? <button type="button" onClick={finish} style={PRIMARY}>Done</button>
              : <button type="button" onClick={() => setI(i + 1)} style={PRIMARY}>Next</button>}
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
}

const GHOST = { border: '.5px solid rgba(0,0,0,.18)', background: '#fff', color: '#6b6a64', fontSize: 12, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit' };
const PRIMARY = { border: 'none', background: '#378ADD', color: '#fff', fontSize: 12, padding: '5px 14px', borderRadius: 7, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 };

// Small reusable trigger button for page headers.
export function GuideButton({ onClick, style }) {
  return (
    <button type="button" onClick={onClick} style={{
      font: 'inherit', fontSize: 12, padding: '4px 11px', border: '.5px solid rgba(0,0,0,.18)',
      borderRadius: 7, background: '#fff', color: '#6b6a64', cursor: 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 5, ...style,
    }}>
      <span aria-hidden style={{ fontWeight: 700, color: '#378ADD' }}>?</span> Guide
    </button>
  );
}
